import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { Typography, Box, CircularProgress, Tooltip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useSnackbar } from 'notistack';
import { getMessage } from 'helpers/helper';

import FS from 'services/fileService';
import M from 'messages';
import classes from './styles';

const WineListImageInput = ({
  label,
  value = null,
  onChange,
  disabled = false,
  error = false,
  helperText = '',
  tooltip = '',
  imageType = 'logo', // 'logo' or 'background'
  acceptFiles = {
    'image/*': ['.jpeg', '.jpg', '.png', '.webp'],
  },
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [preview, setPreview] = useState(value);
  const [loading, setLoading] = useState(false);
  const cropperRef = useRef(null);

  useEffect(() => {
    setPreview(value);
  }, [value]);

  const onFileUpload = async file => {
    if (!file) return;

    setLoading(true);
    try {
      onChange(null);
      const response = await FS.upload(file, imageType);
      const newPreview = URL.createObjectURL(file);
      setPreview(newPreview);
      const respData = response?.data?.response_data;
      let location = '';
      if (Array.isArray(respData)) {
        const original = respData.find(item => item?.originalImage) || respData[0];
        location = original?.Location || '';
      } else {
        location = respData?.Location || '';
      }
      onChange(location);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      enqueueSnackbar(getMessage(error?.response?.data, 'error'), {
        variant: 'error',
      });
    }
  };

  const validateImageSize = file => {
    return new Promise(resolve => {
      const img = new Image();
      img.onload = () => {
        const isValidSize =
          imageType === 'logo'
            ? img.width >= 100 && img.height >= 100 // Logo minimum size
            : img.width >= 100 && img.height >= 100; // Background minimum size

        if (!isValidSize) {
          const message =
            imageType === 'logo' ? M.get('form.errors.logoMinSize') : M.get('form.errors.backgroundMinSize');
          enqueueSnackbar(message, { variant: 'error' });
          resolve(false);
        } else {
          resolve(true);
        }
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const onDropAction = async acceptedFiles => {
    const file = acceptedFiles[0];
    if (file) {
      const isValidSize = await validateImageSize(file);
      if (!isValidSize) return;

      // For background images, open cropper V2 with fixed 2:1 aspect and validate aspect >= 2:1
      if (imageType === 'background') {
        const img = new Image();
        img.onload = async () => {
          const inputAspect = img.width / img.height;
          if (inputAspect < 2) {
            // If provided image is narrower than 2:1, just proceed to crop (no warnings requested),
            // cropper will enforce aspect by zooming to fit width.
          }
          const croppedFile = await cropperRef.current.openCrop(file, { aspectRatio: 2 });
          if (croppedFile) {
            await onFileUpload(croppedFile);
          } else {
            setLoading(false);
          }
        };
        img.src = URL.createObjectURL(file);
        return;
      }

      onFileUpload(file);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onDropAction,
    accept: acceptFiles,
    multiple: false,
    disabled: disabled || loading,
  });

  const onDelete = () => {
    setPreview(null);
    onChange(null);
  };

  const getUploadArea = () => {
    // Hide upload section when image is already selected or while uploading
    if (preview || loading) return null;

    return (
      <Box
        {...getRootProps()}
        sx={{
          ...classes.uploadArea,
          ...(error && classes.errorBorder),
          ...(isDragActive && classes.dragActive),
          ...(imageType === 'background' && classes.backgroundUploadArea),
          ...(disabled && classes.disabledUploadArea),
        }}>
        <input {...getInputProps()} />
        <CloudUploadIcon sx={classes.uploadIcon} />
        <Typography variant="body2" sx={classes.uploadText}>
          {isDragActive
            ? M.get('form.dropImageHere')
            : imageType === 'logo'
              ? M.get('wineLists.personalization.uploadLogo')
              : M.get('wineLists.personalization.uploadBackground')}
        </Typography>
        <Typography variant="caption" sx={classes.uploadHint}>
          {imageType === 'logo' ? M.get('form.logoSizeHint') : M.get('form.backgroundSizeHint')}
        </Typography>
      </Box>
    );
  };

  const getPreviewArea = () => {
    if (!preview && !loading) return null;

    return (
      <Box sx={classes.previewContainer}>
        {loading ? (
          <Box sx={classes.loadingContainer}>
            <CircularProgress size={40} />
            <Typography variant="caption" sx={classes.loadingText}>
              {M.get('form.uploading')}
            </Typography>
          </Box>
        ) : (
          <Box sx={classes.previewBox}>
            {!disabled && (
              <IconButton sx={classes.removeBtn} aria-label="remove image" onClick={onDelete} size="small">
                <RemoveCircleIcon />
              </IconButton>
            )}
            <Box
              component="img"
              sx={{
                ...classes.previewImage,
                ...(imageType === 'background' && classes.backgroundPreview),
              }}
              src={preview}
              alt="preview"
            />
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={classes.container}>
      {/* Hidden cropper for background mode */}
      <Box sx={classes.labelContainer}>
        <Typography variant="body2" sx={classes.label}>
          {label}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip} placement="top">
            <HelpOutlineIcon sx={classes.tooltipIcon} />
          </Tooltip>
        )}
      </Box>

      {imageType === 'logo' && (
        <>
          {getUploadArea()}
          {getPreviewArea()}
        </>
      )}

      {helperText && (
        <Typography
          variant="caption"
          sx={{
            ...classes.helperText,
            ...(error && classes.errorText),
          }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

WineListImageInput.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  tooltip: PropTypes.string,
  imageType: PropTypes.oneOf(['logo']),
  acceptFiles: PropTypes.object,
};

export default WineListImageInput;
